import { Email, Menu, PhoneInTalk } from "@mui/icons-material";
import { SvgIconProps, useMediaQuery, useTheme } from "@mui/material";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import Logo from "src/assets/Logo";
import { useAuth } from "src/auth/AuthContext";
import Feature from "src/common_constants/Feature";
import { useAppSelector } from "src/redux/hooks";
import { selectHasFeature } from "src/redux/slices/featuresSlice";
import { RootState } from "src/redux/store";
import AppVersion from "./AppVersion";
import FloatingBar from "./FloatingBar";
import { SideNav } from "./SideNav";
import UserProfileHeader from "./UserProfileHeader";

interface NavItem {
  title: string;
  path: string;
  icon: React.ReactElement<SvgIconProps>;
  requiredPermission: string;
}

const TopNav = ({
  TOP_NAV_HEIGHT,
  items,
  SIDE_NAV_WIDTH = 280,
}: {
  TOP_NAV_HEIGHT: number;
  items: any[];
  SIDE_NAV_WIDTH?: number;
}) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up("md"));
  const auth = useAuth();
  const [sideNavWidth, setSideNavWidth] = useState(SIDE_NAV_WIDTH);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const handleDrawerToggle = () => {
    setOpen(!open);
  };
  const hasFeatureCallAgent = useAppSelector((state: RootState) =>
    selectHasFeature(state, Feature.AI_CALL_AGENT),
  );
  const toggleSideNav = () => {
    setSideNavWidth(sideNavWidth === SIDE_NAV_WIDTH ? 80 : SIDE_NAV_WIDTH);
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          height: TOP_NAV_HEIGHT,
          backgroundColor: "white",
          color: "#000",
          boxShadow: "0px",
          mt: 0,
          transition: "0.3s",
          transitionTimingFunction: "ease-in-out",
          width: "100%",
        }}
        elevation={0}
      >
        <Container maxWidth={false} disableGutters sx={{ px: 3 }}>
          <Toolbar
            disableGutters
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: { xs: 1, md: 6 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                minWidth: { xs: "40px", sm: "120px" },
                flexShrink: 0,
              }}
            >
              {!smUp && (
                <IconButton onClick={handleDrawerToggle} sx={{ mr: 1 }}>
                  <Menu />
                </IconButton>
              )}
              {smUp && (
                <Box sx={{ display: "block" }}>
                  {auth.logo?.url ? (
                    <img
                      src={auth.logo.url}
                      alt="Client Logo"
                      style={{
                        height: "40px",
                        width: "auto",
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <Logo />
                  )}
                </Box>
              )}
            </Box>

            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                justifyContent: "center",
                flexGrow: 0,
                flexShrink: 1,
                overflow: "hidden",
              }}
            >
              <FloatingBar items={items} />
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                ml: "auto",
                minWidth: { xs: "auto", sm: "200px" },
                justifyContent: "flex-end",
              }}
            >
              <IconButton
                size="small"
                onClick={() => {
                  if (
                    location.pathname.toLowerCase() ===
                    "/emailconversionbookinglog"
                  ) {
                    window.location.reload();
                  } else {
                    navigate("/EmailConversionBookingLog");
                  }
                }}
                title={t("booking.convoLog.titleShort", "Email Booking Conversion Log")}
              >
                <Email sx={{ color: "#002D62" }} />
              </IconButton>
              {!!hasFeatureCallAgent && (
              <IconButton
                size="small"
                onClick={() => {
                  if (location.pathname.toLowerCase() === "/callagent") {
                    window.location.reload();
                  } else {
                    navigate("/CallAgent");
                  }
                }}
                title="Call Agent Notes"
              >
                <PhoneInTalk sx={{ color: "#002D62" }} />
              </IconButton>
              )}
              {/* <IconButton size="small">
                <Badge badgeContent={2} color="success">
                  <NotificationsIcon sx={{ color: "#002D62" }} />
                </Badge>
              </IconButton> */}
              <AppVersion variant="navbar" />
              <UserProfileHeader />
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {!smUp && (
        <SideNav
          open={open}
          onClose={handleDrawerToggle}
          sideNavWidth={sideNavWidth}
          topNavHeight={TOP_NAV_HEIGHT}
          toggleSideNav={toggleSideNav}
        />
      )}
    </>
  );
};

export default TopNav;
